/* eslint-disable react/display-name */
import React, { ReactNode } from 'react';
import classNames from 'classnames';

import { Currency } from '~components/form-fields/inputs';
import Icon, { faCheck, faXmark, IconProp } from '~components/icon';

import { Column } from './types';

/**
 * Generic read-only renderer
 */
export function genericCellRender(mapper: (value: any, row?: any) => ReactNode, wrapInDiv = true, column?: Column) {
  return (value: any, row: any) =>
    wrapInDiv ? (
      <div className={classNames(['cell', 'readonly', column?.classNames?.cell])}>{mapper(value, row)}</div>
    ) : (
      mapper(value, row)
    );
}

/**
 * Checkbox renderer
 */
export function checkboxCellRender(column?: Column) {
  return (value: any, _row: any) => (
    <div className={classNames(['cell', column?.classNames?.cell])}>
      {value === true ? <Icon icon={faCheck} color="green" /> : <Icon icon={faXmark} color="red" />}
    </div>
  );
}

/**
 * Currency renderer
 */
export function currencyCellRender(
  id: string,
  noDecimals = false,
  zeroDisplay?: ((row: any, value: number) => React.ReactNode) | string,
  column?: Column,
) {
  return (value: number, row: any) => {
    const uniqueId = `${id}_${row?.id}`;
    const useZeroDisplay =
      value === null || isNaN(value) || (typeof zeroDisplay === 'function' && zeroDisplay(row, value));

    if (useZeroDisplay) {
      const placeholder = typeof zeroDisplay === 'function' ? zeroDisplay(row, value) : (zeroDisplay ?? '');

      return <div className={classNames(['cell', 'readonly', column?.classNames?.cell])}>{placeholder}</div>;
    }

    return (
      <Currency
        className={classNames(['cell', 'readonly', column?.classNames?.cell])}
        id={uniqueId}
        name={uniqueId}
        type="readonly"
        value={value}
        noDecimals={noDecimals}
        disabled
      />
    );
  };
}

/**
 * Clickable link renderer.
 *
 * When `getHref` is supplied the cell renders a real `<a href>` pointing at the
 * row's URL, so the browser's native link affordances work: right-click "Open in
 * new tab", middle-click and Ctrl/Cmd/Shift+click all open the row in a new tab.
 * A plain (unmodified, primary-button) left-click is intercepted and routed
 * through `onClick` for in-app navigation; modified/non-primary clicks are left
 * for the browser to handle so the new-tab gestures are not swallowed.
 *
 * When `getHref` is omitted the caller opens a modal or fires a programmatic
 * handler rather than navigating to a URL, so the cell renders a
 * `<span role="button">` (no `href`). This avoids advertising an "Open in new
 * tab" option that would lead nowhere and keeps these cells identical to their
 * pre-anchor behaviour.
 */
export function linkCellRender(
  onClick: (row: any) => void,
  hoverIcon?: IconProp,
  column?: Column,
  getHref?: (row: any) => string,
) {
  return (value: any, row: any) =>
    value && (
      <div className={classNames(['on-hover', 'cell', column?.classNames?.cell])}>
        {getHref ? (
          <a
            className="link"
            href={getHref(row)}
            onClick={(e) => {
              // Let the browser handle modifier / non-primary-button clicks so the
              // native new-tab / new-window gestures keep working (ACs 2–4).
              if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
              e.preventDefault();
              onClick(row);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(row);
              }
            }}
          >
            {value}
          </a>
        ) : (
          <span
            className="link"
            role="button"
            tabIndex={0}
            onClick={() => onClick(row)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(row);
              }
            }}
          >
            {value}
          </span>
        )}
        {hoverIcon && <Icon icon={hoverIcon} className="hover ms-2" size="1x" />}
      </div>
    );
}
